<template>
  <Is24Layout
    :class="{ 'map-view': isMapView }"
    :baseUrl="baseUrl"
    :currentLanguage="locale"
    :currentRoute="route"
    hideFooter
    :getLanguageToLink="getLanguageToLink"
    :isTopNavEnabled="isNewNavigationEnabled"
    :isUserLoggedIn="isLoggedIn"
    @trackFooterClick="handleTrackFooterClick"
    @trackHeaderClick="handleTrackHeaderClick"
  >
    <template #headerMetaAction>
      <Is24MainNavItem
        :menuItem="adMenuItem"
        :currentRoute="route"
        :baseUrl="baseUrl"
        :bordered="true"
        @click="handleTrackNavItemClick('open_insertAd', 'tab_navigation')"
      />
    </template>
    <template #headerMetaNavigation>
      <FavouritesLink
        :menuItem="favouritesMenuItem"
        :baseUrl="baseUrl"
        :menuNavItem="Is24MainNavItem"
        :route="route"
        @click="handleTrackNavItemClick('open_favourites', 'tab_navigation')"
      />
      <Is24MainNavItem
        :menuItem="mainHeaderMenuItem"
        icon="PersonOutline"
        :baseUrl="baseUrl"
        :currentRoute="route"
        dropdownOrientation="right"
        @click="handleTrackNavItemClick('open_login', 'tab_navigation')"
      />
    </template>
    <template #sideMenuMetaNavigation>
      <FavouritesLink
        :menuItem="favouritesMenuItem"
        :baseUrl="baseUrl"
        :menuNavItem="Is24SideMenuNavItem"
        :route="route"
        @click="handleTrackNavItemClick('open_favourites', 'tab_navigation')"
      />
    </template>
    <template #sideMenuAccountNavigation>
      <template v-if="isLoggedIn">
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.overviewMenuItem') as unknown as MenuItem"
          icon="PersonOutline"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.settingsMenuItem') as unknown as MenuItem"
          icon="Settings"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.myAdsMenuItem') as unknown as MenuItem"
          icon="Appointment"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.myRealEstateMenuItem') as unknown as MenuItem"
          icon="House"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.searchAlertMenuItem') as unknown as MenuItem"
          icon="Bell"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.tenantPlusMenuItem') as unknown as MenuItem"
          icon="PlusMembership"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
        <Is24SideMenuNavItem
          :menuItem="tm('$is24.nav.harmonize.signOutMenuItem') as unknown as MenuItem"
          icon="SignOut"
          :baseUrl="baseUrl"
          :currentRoute="route"
        />
      </template>
    </template>
    <router-view />
  </Is24Layout>
</template>

<script lang="ts" setup>
import {
  Is24Layout,
  Is24MainNavItem,
  Is24SideMenuNavItem,
  type HgSupportedLanguage,
  type MenuItem,
  type TrackingData,
  type TrackingHeaderData,
} from '@smg-real-estate/domus';
import { setGeoData } from '@smg-real-estate/geo-libs';
import { FavouritesLink } from '@smg-real-estate/seeker-app-shared-components';
import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onServerPrefetch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { fetchSmgSplitSession, setTemporaryUUIDCookie } from '@/api/splitApi';
import useConditionalSSRContext from '@/composables/useConditionalSSRContext';
import { getAuthToken } from '@/helpers/auth/authClient';
import { getAppMetaInfo } from '@/helpers/metaInfoHelper';
import { getLanguageSwitchLink } from '@/router';
import useGeoStore from '@/stores/useGeoStore';
import useMapStore from '@/stores/useMapStore';
import useSplitStore from '@/stores/useSplitStore';
import { trackFooter, trackHeader } from '@/tracking';
import { ViewMode } from '@/types';
import { SplitFeatureFlags } from './stores/splitFeatureFlags';

const splitStore = useSplitStore();
const route = useRoute();
const { viewMode } = storeToRefs(useMapStore());
const { getUrlByGeoLocation } = storeToRefs(useGeoStore());
const { splitSessionKey } = storeToRefs(splitStore);
const { tm, locale } = useI18n();
const ctx = useConditionalSSRContext();

const authToken = ref<string | undefined>();
const isLoggedIn = computed(() => authToken.value !== undefined);

onMounted(async () => {
  window.addEventListener('optanonHandler', async () => {
    setTemporaryUUIDCookie(splitSessionKey.value, document.cookie, window.location.href);

    const smgSplitSessions = await fetchSmgSplitSession();
    splitStore.updateSplitStoreFromApi(smgSplitSessions);
  });
  authToken.value = await getAuthToken();
});

const baseUrl = ref(import.meta.env.VUE_PLATFORM_BASE_URL);

const isMapView = computed((): boolean => {
  return viewMode.value === ViewMode.Map;
});

const isNewNavigationEnabled = computed(() =>
  splitStore.isFeatureOn(SplitFeatureFlags.DD4533_NEW_TOP_LEVEL_NAVIGATION),
);

const adMenuItem = computed((): MenuItem => {
  return tm(
    isNewNavigationEnabled.value
      ? '$is24.nav.topNavNewListingMenuItem'
      : '$is24.nav.harmonize.newListingMenuItem',
  ) as unknown as MenuItem;
});

const userAccountMenuItem = computed((): MenuItem => {
  return {
    label: tm('$is24.nav.harmonize.overviewMenuItem.label') as string,
    href: `${import.meta.env.VUE_PLATFORM_ACCOUNT_BASE_URL}`,
  };
});

const mainHeaderMenuItem = computed((): MenuItem => {
  if (isLoggedIn.value) {
    return {
      label: tm('$is24.nav.loggedInMenuItem.label') as string,
      children: tm('$is24.nav.harmonize.privateItems'),
    } as unknown as MenuItem;
  }
  return userAccountMenuItem.value;
});

const favouritesMenuItem = computed((): MenuItem => {
  return tm('$is24.nav.favoriteMenuItem') as unknown as MenuItem;
});

const getLanguageToLink = (language: HgSupportedLanguage) => {
  if (typeof route.meta?.getLanguageSwitchLink === 'function') {
    return route.meta.getLanguageSwitchLink(route, language, getUrlByGeoLocation.value[language]);
  }
  return getLanguageSwitchLink(route, language);
};

const handleTrackHeaderClick = (data: {
  eventAction: string;
  eventLabel: string;
  contentLanguage?: string;
}) => {
  trackHeader({
    eventAction: data.eventAction,
    eventLabel: data.eventLabel,
    contentLanguage: data.contentLanguage ?? locale.value,
  });
};

const handleTrackFooterClick = (data: TrackingData) => {
  trackFooter(data);
};

const handleTrackNavItemClick = (action: string, label: string) => {
  const trackingData: TrackingHeaderData = {
    eventAction: action,
    eventLabel: label,
    contentLanguage: locale.value,
  };
  trackHeader(trackingData);
};

useHead(() => {
  return getAppMetaInfo(locale.value as HgSupportedLanguage);
});

onServerPrefetch(async () => {
  splitStore.updateSplitStoreFromApi(ctx!.smgSplitSession);
  // Set geo-data from layer added by the lambda
  setGeoData(ctx!.geoData);
});
</script>
<style lang="scss">
@use '@/styles/global-is24' as *;
</style>

<style lang="scss" scoped>
@use '@/styles/variables' as *;

.map-view {
  min-height: auto;
}
</style>
